import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconBoltOutline18 as Zap } from "nucleo-ui-outline-18"
import {
	Cell,
	Pie,
	PieChart,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
} from "recharts"

import { cn } from "@productlane/lib/src/browser"
import { Avatar, Empty, Tooltip } from "@productlane/ui"
import { AgentOrb } from "@productlane/ui/src/components/agent-orb/agent-orb"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@productlane/ui/src/components/basic-table"

import type { ReportingQueryProps } from "@/screens/app/reporting/reporting-query"
import EmptyPeople from "@/assets/empty-states/empty-people.png"
import { api } from "@/lib/api"

const AGENT_COLORS = [
	"rgba(76, 183, 130, 1)", // #4CB782 100% - matches Productlane Agent in conversations
	"rgba(76, 183, 130, 0.6)", // #4CB782 60% - matches Other Conversations
	"rgba(76, 183, 130, 0.45)", // #4CB782 45% (60% - 15%)
	"rgba(76, 183, 130, 0.3)", // #4CB782 30% (45% - 15%)
	"rgba(76, 183, 130, 0.85)", // #4CB782 85% (100% - 15%)
	"rgba(76, 183, 130, 0.7)", // #4CB782 70% (85% - 15%)
]

const AiAgentAvatar = () => <AgentOrb size={24} />

interface AgentPerformanceProps extends ReportingQueryProps {
	className?: string
}

export const AgentPerformance = ({
	className,
	startDate,
	endDate,
	...filters
}: AgentPerformanceProps) => {
	const navigate = useNavigate()
	const metricsQuery = api.workspace.getAgentPerformanceMetrics.useQuery(
		{ startDate, endDate, ...filters },
		{
			cacheTime: Infinity,
			staleTime: Infinity,
		},
	)

	const agentMetrics = metricsQuery.data?.agents
	const aiAgent = metricsQuery.data?.aiAgent

	// Sort agents by resolved count (descending), including Productlane Agent
	const sortedAgents = useMemo(() => {
		if (!agentMetrics) return []
		const allAgents = [...agentMetrics]
		if (aiAgent) {
			allAgents.push(aiAgent)
		}
		return allAgents.sort((a, b) => b.resolved - a.resolved)
	}, [agentMetrics, aiAgent])

	// Calculate donut chart data - one segment per agent + Productlane Agent
	const donutData = useMemo(() => {
		if (!agentMetrics) return []

		const agentSegments = agentMetrics
			.filter((agent) => agent.assigned > 0)
			.map((agent) => ({
				name: agent.name || "Unknown",
				value: agent.assigned,
				imageUrl: agent.imageUrl,
				id: agent.id,
				isUnassigned: false,
				isAiAgent: false,
			}))

		// Add Productlane Agent segment if there are AI chat threads
		if (aiAgent && aiAgent.assigned > 0) {
			agentSegments.push({
				name: "Productlane Agent",
				value: aiAgent.assigned,
				imageUrl: null,
				id: "ai-agent",
				isUnassigned: false,
				isAiAgent: true,
			})
		}

		// Sort by value descending so largest segment appears first
		const sorted = agentSegments.sort((a, b) => b.value - a.value)

		// Assign colors after sorting - highest opacity for largest value
		return sorted.map((segment, index) => ({
			...segment,
			color:
				AGENT_COLORS[index % AGENT_COLORS.length] || "rgba(53, 216, 206, 1)",
		}))
	}, [agentMetrics, aiAgent])

	if (!agentMetrics) {
		return (
			<div className={className}>
				<div className="px-6 pt-4">
					<div className="grid grid-cols-1 lg:grid-cols-3">
						{/* Agent table skeleton - 2/3 width */}
						<div className="col-span-2 pb-6 pt-1">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="w-8 pl-1 text-xs font-medium text-gray-500 dark:text-gray-400">
											#
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											Agent
										</TableHead>
										<TableHead className="w-36 text-xs font-medium text-gray-500 dark:text-gray-400">
											Assigned
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											TTFR
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											TTR
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{[1, 2, 3, 4, 5].map((i) => (
										<TableRow key={i}>
											<TableCell className="pl-1 text-sm font-medium text-gray-500 dark:text-gray-400">
												<div className="h-4 w-6 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-3">
													<div className="h-8 w-8 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
													<div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
												</div>
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												<div className="h-4 w-12 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												<div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												<div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>

						{/* Donut chart skeleton - 1/3 width */}
						<div className="flex items-center justify-center p-6">
							<div className="relative">
								<div className="h-36 w-36 animate-pulse rounded-full border-8 border-gray-200 dark:border-gray-700"></div>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	interface DonutTooltipProps {
		active?: boolean
		payload?: Array<{
			value: number
			name: string
			payload?: {
				imageUrl?: string
				name: string
				value: number
				isUnassigned?: boolean
				isAiAgent?: boolean
			}
		}>
	}

	const DonutTooltip = ({ active, payload }: DonutTooltipProps) => {
		if (active && payload?.[0]) {
			const data = payload[0]
			const agentData = data.payload
			const total = donutData.reduce((sum, item) => sum + item.value, 0)
			const percentage =
				total > 0 ? ((data.value / total) * 100).toFixed(1) : "0"

			return (
				<div className="rounded-lg bg-white p-3 text-xs ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
					{agentData?.isAiAgent ? (
						<div className="flex items-center gap-2">
							<AiAgentAvatar />
							<div>
								<p className="font-medium text-primary">Productlane Agent</p>
								<p className="text-secondary">
									{data.value} threads ({percentage}%)
								</p>
							</div>
						</div>
					) : (
						<div className="flex items-center gap-2">
							<Avatar
								image={agentData?.imageUrl}
								name={agentData?.name || "Unknown"}
								size="sm"
								roundness="full"
							/>
							<div>
								<p className="font-medium text-primary">
									{agentData?.name || "Unknown"}
								</p>
								<p className="text-secondary">
									{data.value} threads ({percentage}%)
								</p>
							</div>
						</div>
					)}
				</div>
			)
		}
		return null
	}

	const hasAgents = sortedAgents.length > 0 || metricsQuery.isLoading

	return (
		<div className={className}>
			<div className="px-5 pb-5 pt-2">
				{hasAgents ? (
					<div className="grid grid-cols-1 lg:grid-cols-3">
						{/* Agent Table - 2/3 width */}
						<div className="col-span-2 pb-6 pt-1">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="w-8 pl-1 text-xs font-medium text-gray-500 dark:text-gray-400">
											#
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											Agent
										</TableHead>
										<TableHead className="w-36 text-xs font-medium text-gray-500 dark:text-gray-400">
											Assigned
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											<Tooltip label="Time To First Reply">
												<span>TTFR</span>
											</Tooltip>
										</TableHead>
										<TableHead className="text-xs font-medium text-gray-500 dark:text-gray-400">
											<Tooltip label="Time To Reply">
												<span>TTR</span>
											</Tooltip>
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedAgents.map((agent, index) => (
										<TableRow
											key={agent.id}
											className={cn(
												agent.isAiAgent &&
													"hover:bg-gray-50 dark:hover:bg-gray-800/50",
											)}
											onClick={() => {
												if (agent.isAiAgent) {
													void navigate({ to: "/settings/ai/agent" })
												}
											}}
										>
											<TableCell className="pl-1 text-sm font-medium text-gray-500 dark:text-gray-400">
												#{index + 1}
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-3">
													{agent.isAiAgent ? (
														<AiAgentAvatar />
													) : (
														<Avatar
															image={agent.imageUrl}
															name={agent.name || "Unknown"}
															size="s"
															roundness="full"
														/>
													)}
													<span className="text-sm font-medium text-gray-900 dark:text-white">
														{agent.isAiAgent
															? "Productlane Agent"
															: agent.name || "Unknown"}
													</span>
												</div>
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												{agent.assigned}
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												{agent.isAiAgent ? (
													<Zap className="h-4 w-4 text-secondary" />
												) : (
													<Tooltip label="Time To First Reply">
														<span>{agent.ttfr}</span>
													</Tooltip>
												)}
											</TableCell>
											<TableCell className="text-sm text-gray-900 dark:text-white">
												{agent.isAiAgent ? (
													<Zap className="h-4 w-4 text-secondary" />
												) : (
													<Tooltip label="Time To Reply">
														<span>{agent.ttr}</span>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>

						{/* Donut Chart - 1/3 width */}
						<div className="flex items-center justify-center p-6">
							<div className="relative">
								<div className="h-36 w-36">
									<ResponsiveContainer width="100%" height="100%">
										<PieChart>
											<Pie
												data={donutData}
												cx="50%"
												cy="50%"
												innerRadius={44}
												outerRadius={56}
												paddingAngle={0}
												dataKey="value"
												stroke="none"
												startAngle={90}
												endAngle={-270}
											>
												{donutData.map((entry, index) => (
													<Cell key={`cell-${index}`} fill={entry.color} />
												))}
											</Pie>
											<RechartsTooltip content={<DonutTooltip />} />
										</PieChart>
									</ResponsiveContainer>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="flex justify-center py-12">
						<Empty
							title="No team activity"
							text="Track response times and workload distribution across your team within the selected date range."
							className="max-w-[22rem]"
							image={
								<img
									width={130}
									height={128}
									src={EmptyPeople}
									alt="Empty team activity"
								/>
							}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
